import { useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { User } from 'referral-types';
import { BROADCAST_USER_CHANNEL, BroadcastChannelActions, LOCAL_STORAGE_USER_KEY } from '../../constants';
import { AuthContext } from './AuthContext';
import { refreshAuthSession } from '../../api/service';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | undefined>(() =>
    localStorage.getItem(LOCAL_STORAGE_USER_KEY) !== null
      ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_USER_KEY)!)
      : undefined
  );
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const refreshedOnMountRef = useRef<boolean>(false);

  const updateCurrentUser = useCallback((user?: User) => {
    setCurrentUser(user);

    new BroadcastChannel(BROADCAST_USER_CHANNEL).postMessage({
      type: BroadcastChannelActions.UPDATE_USER,
      user: user,
    });

    if (user) {
      localStorage.setItem(LOCAL_STORAGE_USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(LOCAL_STORAGE_USER_KEY);
    }
  }, []);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const startRefreshTimer = useCallback(() => {
    clearRefreshTimer();

    // Proactively refresh every 50 minutes to stay ahead of 1-hour access token expiry.
    // This ensures users stay logged in for days without forced re-authentication.
    const refreshInterval = 50 * 60 * 1000;

    refreshTimerRef.current = setInterval(async () => {
      try {
        const refreshedUser = await refreshAuthSession();
        updateCurrentUser(refreshedUser);
      } catch (error) {
        console.warn('Proactive token refresh failed:', error);
        // Silently fail; reactive refresh in service interceptor will handle it on next request
      }
    }, refreshInterval);
  }, [clearRefreshTimer, updateCurrentUser]);

  const clearUserInfo = useCallback(() => {
    document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    localStorage.removeItem('accessToken');
    clearRefreshTimer();
    updateCurrentUser(undefined);
  }, [updateCurrentUser, clearRefreshTimer]);

  useEffect(() => {
    if (currentUser == null) {
      clearRefreshTimer();
    } else {
      startRefreshTimer();
    }
  }, [currentUser, clearRefreshTimer, startRefreshTimer]);

  // On app load, refresh tokens immediately if user exists to avoid stale access tokens
  // after tab reload or switching between tabs.
  useEffect(() => {
    if (currentUser && !refreshedOnMountRef.current) {
      refreshedOnMountRef.current = true;
      refreshAuthSession()
        .then((u) => updateCurrentUser(u))
        .catch(() => {
          // Silently fail; reactive refresh in service interceptor will handle it on next request
        });
    }
  }, [updateCurrentUser]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearRefreshTimer();
    };
  }, [clearRefreshTimer]);

  // update user if changed in other tabs
  useEffect(() => {
    const userChannel = new BroadcastChannel(BROADCAST_USER_CHANNEL);
    const handleMessage = (message: MessageEvent) => {
      if (message.data.type === BroadcastChannelActions.UPDATE_USER) {
        setCurrentUser(message.data.user);
      }
    };

    userChannel.addEventListener('message', handleMessage);

    return () => {
      userChannel.removeEventListener('message', handleMessage);
      userChannel.close();
    };
  }, [updateCurrentUser]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isSignedIn: currentUser != null,
        setCurrentUser: updateCurrentUser,
        clearUserInfo,
        isAdmin: currentUser != null && currentUser?.roles.includes('Admin'),
        isMember: currentUser != null && currentUser?.roles.includes('Member'),
        isInfluencer: currentUser != null && currentUser?.roles.includes('Influencer'),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
